"use client"

import { useForm } from "@inertiajs/react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { FileUpload } from "@/components/ui/file-upload"
import { HintText, Input, Label } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  adminOnwardsDestinationPath,
  adminOnwardsDestinationsPath,
} from "@/routes"

export interface DestinationDetail {
  param?: string
  kind: string
  name: string
  key: string
  status: string
  summary: string | null
  action_label: string | null
  image_url?: string | null
  minimum_age?: number | null
  shared_data_fields?: string[]
  deep_link?: string | null
  url?: string | null
  crisis?: boolean | null
  opens_at?: string | null
  closes_at?: string | null
  contact_methods?: { kind: string; target: string }[]
}

interface Props {
  mode: "new" | "edit"
  kind: string
  statuses: string[]
  initial?: DestinationDetail
}

interface ContactMethod {
  kind: string
  target: string
  _destroy?: boolean
}

interface FormShape {
  kind: string
  destination: {
    name: string
    key: string
    status: string
    summary: string
    action_label: string
    card_image?: File | null
    url?: string
    deep_link?: string
    minimum_age?: string
    shared_data_fields?: string[]
    crisis?: boolean
    opens_at?: string
    closes_at?: string
    contact_methods_attributes?: ContactMethod[]
  }
}

export function DestinationForm({ mode, kind, statuses, initial }: Props) {
  const { data, setData, post, patch, processing, errors } = useForm<FormShape>(
    {
      kind,
      destination: {
        name: initial?.name ?? "",
        key: initial?.key ?? "",
        status: initial?.status ?? "draft",
        summary: initial?.summary ?? "",
        action_label: initial?.action_label ?? "",
        url: initial?.url ?? "",
        deep_link: initial?.deep_link ?? "",
        minimum_age: initial?.minimum_age?.toString() ?? "",
        shared_data_fields: initial?.shared_data_fields ?? [],
        crisis: initial?.crisis ?? false,
        opens_at: initial?.opens_at ?? "",
        closes_at: initial?.closes_at ?? "",
        contact_methods_attributes: initial?.contact_methods ?? [],
      },
    },
  )

  const d = data.destination
  const patchDest = (patchData: Partial<FormShape["destination"]>) =>
    setData("destination", { ...d, ...patchData })

  const [cardPreview, setCardPreview] = useState<string | null>(
    initial?.image_url ?? null,
  )

  const handleCardSelect = (files: FileList) => {
    const file = files[0]
    if (!file) return
    patchDest({ card_image: file })
    const reader = new FileReader()
    reader.onload = (e) => setCardPreview(e.target?.result as string)
    reader.readAsDataURL(file)
  }

  // Replace-only: clearing drops the key from the payload entirely (a nil
  // assignment would detach the server-side attachment), so submitting with
  // no new file leaves whatever is on the server untouched.
  const handleCardClear = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { card_image, ...rest } = d
    setData("destination", rest)
    setCardPreview(null)
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const opts =
      d.card_image instanceof File ? { forceFormData: true as const } : {}
    if (mode === "new") post(adminOnwardsDestinationsPath(), opts)
    else if (initial?.param)
      patch(adminOnwardsDestinationPath(initial.param), opts)
  }

  const err = (field: string) => {
    const value = (errors as Record<string, unknown>)[field]
    return Array.isArray(value)
      ? value.join(", ")
      : (value as string | undefined)
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-6">
      <Input
        isRequired
        label="Name"
        value={d.name}
        onChange={(v) => patchDest({ name: v })}
        hint={err("name")}
      />
      <Input
        isRequired
        label="Key"
        hint={err("key") ?? "Stable identifier, e.g. qlife, most, 13yarn."}
        value={d.key}
        onChange={(v) => patchDest({ key: v })}
      />
      <Input
        label="Summary"
        hint="One line shown on the outro tile."
        value={d.summary}
        onChange={(v) => patchDest({ summary: v })}
      />
      <Input
        label="Action label"
        hint="The button text, e.g. 'Start a webchat' or 'Call 13YARN'."
        value={d.action_label}
        onChange={(v) => patchDest({ action_label: v })}
      />

      <div className="flex flex-col gap-1.5">
        <Label>Card image</Label>
        {cardPreview ? (
          <div className="relative overflow-hidden rounded-lg">
            <img
              src={cardPreview}
              alt="Card image preview"
              className="aspect-video w-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition hover:opacity-100">
              <Button
                type="button"
                color="secondary"
                size="sm"
                onClick={handleCardClear}
              >
                Replace image
              </Button>
            </div>
          </div>
        ) : (
          <FileUpload.Root>
            <FileUpload.DropZone
              accept="image/png,image/jpeg"
              allowsMultiple={false}
              hint="PNG or JPG, 1280×720 (16:9) — shown on the outro card"
              maxSize={5 * 1024 * 1024}
              onDropFiles={handleCardSelect}
            />
          </FileUpload.Root>
        )}
        {err("card_image") && (
          <HintText isInvalid>{err("card_image")}</HintText>
        )}
      </div>

      {kind === "resource_link" && (
        <Input
          isRequired
          label="URL"
          value={d.url ?? ""}
          onChange={(v) => patchDest({ url: v })}
          hint={err("url")}
        />
      )}

      {kind === "online_service" && (
        <>
          <Input
            isRequired
            label="Deep link"
            value={d.deep_link ?? ""}
            onChange={(v) => patchDest({ deep_link: v })}
            hint={err("deep_link")}
          />
          <Input
            type="number"
            label="Minimum age (gate)"
            hint="Leave blank if anyone can use it. Set the youngest allowed age otherwise."
            value={d.minimum_age ?? ""}
            onChange={(v) => patchDest({ minimum_age: v })}
          />
          <Input
            label="Consent fields (comma-separated)"
            hint="Fields the young person approves before we pass them on, e.g. declared_age, origin. Leave blank for no consent step."
            value={(d.shared_data_fields ?? []).join(", ")}
            onChange={(v) =>
              patchDest({
                shared_data_fields: v
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
        </>
      )}

      {kind === "helpline" && (
        <>
          <Select
            label="Crisis line"
            selectedKey={d.crisis ? "yes" : "no"}
            onChange={(key) => patchDest({ crisis: key === "yes" })}
            items={[
              { id: "no", label: "No" },
              {
                id: "yes",
                label: "Yes (never shown as closed, never auto-continued)",
              },
            ]}
          >
            {(item) => (
              <Select.Item key={item.id} id={item.id}>
                {item.label}
              </Select.Item>
            )}
          </Select>
          <div className="flex gap-4">
            <Input
              type="time"
              label="Opens at"
              hint="Blank = 24/7"
              value={d.opens_at ?? ""}
              onChange={(v) => patchDest({ opens_at: v })}
            />
            <Input
              type="time"
              label="Closes at"
              value={d.closes_at ?? ""}
              onChange={(v) => patchDest({ closes_at: v })}
            />
          </div>
          <ContactMethods
            methods={d.contact_methods_attributes ?? []}
            onChange={(methods) =>
              patchDest({ contact_methods_attributes: methods })
            }
          />
        </>
      )}

      <Select
        label="Status"
        selectedKey={d.status}
        onChange={(key) => key != null && patchDest({ status: String(key) })}
        items={statuses.map((s) => ({ id: s, label: s }))}
      >
        {(item) => (
          <Select.Item key={item.id} id={item.id} className="capitalize">
            {item.label}
          </Select.Item>
        )}
      </Select>

      <div className="flex justify-end">
        <Button
          type="submit"
          size="lg"
          isDisabled={processing}
          isLoading={processing}
        >
          {mode === "new" ? "Create destination" : "Save changes"}
        </Button>
      </div>
    </form>
  )
}

function ContactMethods({
  methods,
  onChange,
}: {
  methods: ContactMethod[]
  onChange: (methods: ContactMethod[]) => void
}) {
  const visible = methods.filter((m) => !m._destroy)
  return (
    <div className="flex flex-col gap-3">
      <span className="text-secondary text-sm font-medium">
        Contact methods
      </span>
      {visible.map((method, i) => (
        <div key={i} className="flex items-end gap-3">
          <Select
            aria-label="Channel"
            selectedKey={method.kind}
            onChange={(key) =>
              onChange(
                methods.map((m) =>
                  m === method ? { ...m, kind: String(key) } : m,
                ),
              )
            }
            items={[
              { id: "phone", label: "Phone" },
              { id: "webchat", label: "Webchat" },
            ]}
            className="w-36"
          >
            {(item) => (
              <Select.Item key={item.id} id={item.id}>
                {item.label}
              </Select.Item>
            )}
          </Select>
          <Input
            aria-label="Target"
            placeholder="tel:139276 or a webchat URL"
            value={method.target}
            onChange={(v) =>
              onChange(
                methods.map((m) => (m === method ? { ...m, target: v } : m)),
              )
            }
            className="flex-1"
          />
          <Button
            color="link-destructive"
            size="sm"
            onClick={() =>
              onChange(
                methods.map((m) =>
                  m === method ? { ...m, _destroy: true } : m,
                ),
              )
            }
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        color="secondary"
        size="sm"
        onClick={() => onChange([...methods, { kind: "phone", target: "" }])}
        className="self-start"
      >
        Add contact method
      </Button>
    </div>
  )
}
